import { CloseButton } from "@components/buttons/close";
import { InformationButton } from "@components/buttons/information";
import {
  SHAutocomplete,
  SHButton,
  SHDialog,
  SHLabel,
  SHRichTextEditor,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFeatureTemplate } from "@hooks/useFeatureTemplate";
import { usePractice } from "@hooks/usePractice";
import { useUserPermissions } from "@hooks/userUserPermission";
import { useSupplier } from "@hooks/useSupplier";
import {
  CreateFeatureTemplateDTO,
  FeatureTemplateDTO,
} from "@models/feature-template/entities/featureTemplate";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from "@mui/material";
import { AnalysisSaveFeatureSelectionTemplateConfigs } from "@pages/platform-analysis-v2/_id/configs";
import { RootState } from "@redux/store";
import { take } from "lodash";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";
import { ConfirmOverwriteTemplateDialog } from "../confirm-overwrite-template";
import {
  compiledFeatureAnalysisSelections,
  saveFeatureSelectionAsTemplateValidators,
} from "./utils";

type Props = {
  open?: boolean;
  isFeatureAnalysisStep?: boolean;
  onClose?: () => void;
};

export const SaveFeatureSelectionAsTemplateDialog = ({
  open,
  isFeatureAnalysisStep,
  onClose,
}: Props) => {
  const { palette } = useTheme();

  const {
    control,
    reset,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm<FeatureTemplateDTO>({
    mode: "onChange",
    defaultValues: { ...new FeatureTemplateDTO() },
    resolver: yupResolver(saveFeatureSelectionAsTemplateValidators),
  });

  const { user, isSupplierGroup, isAdviserGroup } = useUserPermissions();
  const { supplierData } = useSupplier();
  const { practice: practiceData } = usePractice();
  const { createFeatureTemplate, updateFeatureTemplate } = useFeatureTemplate();

  const storedFeatureSelectionSections = useSelector(
    (state: RootState) => state.platformAnalysis.featureSelections?.sections,
  );
  const storedExistingFeatureTemplate = useSelector(
    (state: RootState) => state.featureTemplate.existingFeatureTemplate,
  );
  const storedFeatureAnalysisSections = useSelector(
    (state: RootState) =>
      state.platformAnalysis.platformAnalysis?.feature?.all?.sections,
  );
  const storedConfigurationVersion = useSelector(
    (state: RootState) =>
      state.platformAnalysis.platformAnalysis?.configurationVersion,
  );

  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const [overwriteId, setOverwriteId] = useState<string | undefined>(undefined);
  const [numOfDisplayingOptions, setNumOfDisplayingOptions] =
    useState<number>(0);

  const handleOnModalClose = () => {
    onClose?.();
    reset();
  };

  const handleVerifySubmit = () => {
    const { name } = getValues();
    if (!name) return;

    const overwriteId = storedExistingFeatureTemplate?.find(
      (item) => item?.name === name,
    )?.id;
    if (overwriteId) {
      setOverwriteId(overwriteId);
      return;
    }

    toggleSubmitting(true);
    setTimeout(() => handleOnSubmit(), 0);
  };

  const handleOnSubmit = async () => {
    const { name: templateName, notes: templateNotes } = getValues();
    const { auth0Id: userId, name: userName, avatarUrl, userType } = user || {};
    const { id: supplierId, companyName: supplierName } = supplierData || {};
    const { id: practiceId, name: practiceName } = practiceData || {};
    const firmId = isAdviserGroup
      ? practiceId
      : isSupplierGroup
      ? supplierId
      : "";
    const firmName = isAdviserGroup
      ? practiceName
      : isSupplierGroup
      ? supplierName
      : "";
    const compiledSections = !isFeatureAnalysisStep
      ? storedFeatureSelectionSections
      : compiledFeatureAnalysisSelections(storedFeatureAnalysisSections || []);
    const defaultPayload: Partial<CreateFeatureTemplateDTO> = {
      name: templateName,
      notes: templateNotes,
      ownerId: userId,
      ownerName: userName,
      ownerAvatarUrl: avatarUrl,
      ownerType: userType,
      supplierId: supplierId,
      supplierName: supplierName,
      sections: compiledSections,
    };

    const response = overwriteId
      ? await updateFeatureTemplate({
          ...defaultPayload,
          id: overwriteId,
        })
      : await createFeatureTemplate(
          {
            ...defaultPayload,
            firmId: firmId ?? "",
            firmName: firmName ?? "",
            forSupplier: isSupplierGroup,
            configurationVersion: storedConfigurationVersion,
          },
          isSupplierGroup,
          true,
        );

    toggleSubmitting(false);
    setOverwriteId(undefined);

    if (!response) return;
    handleOnModalClose();
  };

  return (
    <form>
      <SHDialog
        open={!!open}
        onClose={handleOnModalClose}
        maxWidth={false}
        sx={{
          "& .MuiPaper-root": {
            width: { xs: "100%", md: 656 },
          },
        }}
      >
        <DialogTitle>
          <SHTypography variant="h2" fontWeight={700} lineHeight={"100%"}>
            Save the current selection as a template?
          </SHTypography>

          <SHStack
            sx={{
              position: "absolute",
              top: "22px",
              right: "25px",
            }}
          >
            <CloseButton
              width="12px"
              height="12px"
              onClick={handleOnModalClose}
            />
          </SHStack>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: 0,
            overflowY: "var(--overflow-overlay)",
          }}
        >
          <SHStack>
            <SHTypography
              variant="body2"
              sx={{
                fontSize: "16px",
                margin: "0 40px",
                color: `${palette.text.disabled}`,
              }}
            >
              Fill these fields to help you find the template in the future. You
              can overwrite an existing template by giving it the same name.
            </SHTypography>

            <SHStack
              spacing={3}
              direction="column"
              sx={{ width: { xs: "100%", md: 520 } }}
            >
              <Controller
                control={control}
                name="name"
                render={({ field: { onChange, value } }) => {
                  return (
                    <SHStack gap={0}>
                      <SHLabel label="Template name" required />
                      <SHAutocomplete
                        freeSolo
                        disableCloseOnSelect
                        disabled={isSubmitting}
                        options={storedExistingFeatureTemplate ?? []}
                        filterOptions={(options) => {
                          const currentValue = value?.toLowerCase() || "";
                          const filtered =
                            options?.filter((option) =>
                              option.name
                                ?.toLowerCase()
                                ?.includes(currentValue),
                            ) ?? [];

                          setNumOfDisplayingOptions(filtered?.length || 0);
                          return take(
                            filtered,
                            AnalysisSaveFeatureSelectionTemplateConfigs.MaxTemplatesToDisplay,
                          );
                        }}
                        getOptionLabel={(option) =>
                          typeof option === "string"
                            ? option
                            : option?.name ?? ""
                        }
                        onInputChange={(_, newValue) => {
                          onChange(newValue);
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li
                              {...props}
                              key={option.id}
                              style={{ padding: "10px 10px" }}
                            >
                              <SHStack
                                alignItems={"center"}
                                direction="row"
                                spacing={1}
                              >
                                <SHTypography variant="body3" fontWeight={500}>
                                  {`${option.name} - ${option.selectedFeatures}/${option.totalFeatures} features selected`}
                                </SHTypography>
                                {option?.notes && (
                                  <InformationButton content={option?.notes} />
                                )}
                              </SHStack>
                            </li>
                          );
                        }}
                        textFieldProps={{
                          placeholder: "Enter name of template",
                          error: !!errors.name,
                          helperText: errors?.name?.message,
                          required: true,
                          hasCounter: true,
                          maxLength: 50,
                        }}
                        PaperComponent={
                          numOfDisplayingOptions > 0 ? undefined : () => null
                        }
                        sx={{ width: { xs: "100%", md: 520 } }}
                      />
                    </SHStack>
                  );
                }}
              />

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <SHRichTextEditor
                    {...field}
                    sx={{ width: "100% important" }}
                    label="Notes"
                    placeHolder="Enter notes"
                    maxLength={500}
                    height={165}
                  />
                )}
              />
            </SHStack>
          </SHStack>
        </DialogContent>

        <DialogActions>
          <SHStack
            width={"100%"}
            justifyContent="center"
            alignItems="center"
            direction={"row"}
            spacing={7}
            marginTop={"25px"}
            marginBottom={"15px"}
          >
            <SHButton
              color="primary"
              size="large"
              variant="contained"
              onClick={handleVerifySubmit}
              disabled={isSubmitting || !isValid || !isDirty}
              isLoading={isSubmitting}
              sx={{ width: "410px" }}
            >
              Save as template
            </SHButton>
          </SHStack>
        </DialogActions>
      </SHDialog>

      <ConfirmOverwriteTemplateDialog
        open={!!overwriteId}
        onClose={() => setOverwriteId(undefined)}
        onSubmit={() => {
          toggleSubmitting(true);
          setTimeout(() => handleOnSubmit(), 0);
        }}
        isSubmitting={isSubmitting}
      />
    </form>
  );
};
